// V14.12 — Cloudflare Turnstile React widget.
//
// Renders an invisible-ish challenge that produces a token for our
// backend to verify. Adheres to the integration spec:
//   • Reset on every failure (parent calls `resetWidget` via ref).
//   • Auto-theme switching (light/dark follows Shadcn theme).
//   • Disabled-by-flag for local dev: REACT_APP_TURNSTILE_ENABLED=false
//     → component renders nothing and onVerify() fires with token=null
//     so the form submit button stays enabled.
//   • Never logs tokens.
//
// Usage:
//   const widgetRef = useRef(null);
//   const [token, setToken] = useState('');
//   <TurnstileWidget ref={widgetRef} action="login" onVerify={setToken} />
//   // On failure: widgetRef.current?.reset();
import React, { useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';

// V14.12.1 — Coerce env vars to plain primitives at module-eval time.
// CRA/craco's webpack DefinePlugin replaces `process.env.X` literally with the
// string value, but ONLY when accessed directly inline. Some Docker builds
// have been observed to substitute the whole `process.env` object when the
// variable is "captured" through intermediate references — that's how the
// "expected 'string', got 'object'" error in production logs happens.
// Reading the var inline + String()-coercing guarantees a primitive string.
const SITE_KEY = String(process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim();
const ENABLED = String(process.env.REACT_APP_TURNSTILE_ENABLED || 'true').toLowerCase() !== 'false' && SITE_KEY.length > 0;

const TurnstileWidget = forwardRef(({ action = 'login', onVerify, onError, theme }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);

  // Keep latest callbacks without re-rendering the widget
  onVerifyRef.current = onVerify;
  onErrorRef.current = onError;

  const detectTheme = useCallback(() => {
    if (theme) return theme;
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    }
    return 'auto';
  }, [theme]);

  // Render once on mount
  useEffect(() => {
    if (!ENABLED) {
      // Dev mode: simulate immediate "verify" with no token so submit
      // button doesn't get permanently disabled.
      onVerifyRef.current?.('');
      return;
    }
    let cancelled = false;

    const render = () => {
      if (cancelled || !window.turnstile || !containerRef.current) return;
      try {
        // V14.12.1 — Read the env var inline here too. If something nuked
        // SITE_KEY at module-load time (Docker build edge case), we still
        // get the inlined value from DefinePlugin at this call site.
        const sitekey = String(process.env.REACT_APP_TURNSTILE_SITE_KEY || SITE_KEY || '').trim();
        if (!sitekey) {
          onErrorRef.current?.();
          return;
        }
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey,
          action: String(action || 'login'),
          theme: detectTheme(),
          callback: (token) => onVerifyRef.current?.(token),
          'expired-callback': () => onVerifyRef.current?.(''),
          'error-callback': () => {
            onVerifyRef.current?.('');
            onErrorRef.current?.();
          },
        });
      } catch {
        onErrorRef.current?.();
      }
    };

    if (window.turnstile) {
      render();
    } else {
      // Poll for the global until the async script tag finishes loading.
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          render();
        }
      }, 200);
      const timeout = setTimeout(() => clearInterval(interval), 10000);
      return () => { cancelled = true; clearInterval(interval); clearTimeout(timeout); };
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* noop */ }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  useImperativeHandle(ref, () => ({
    reset() {
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.reset(widgetIdRef.current); } catch { /* noop */ }
      }
      onVerifyRef.current?.('');
    },
  }), []);

  if (!ENABLED) return null;
  return <div ref={containerRef} data-testid={`turnstile-${action}`} className="cf-turnstile" />;
});

TurnstileWidget.displayName = 'TurnstileWidget';
export default TurnstileWidget;
export const isTurnstileEnabled = ENABLED;
